import { useState } from 'react';
import type { SoluuDevice } from '@soluu/ble-core';

import { StoredCredentialsReadout } from './StoredCredentialsReadout.js';
import type { AttemptAnomaly } from '../core/types.js';

interface Props {
  /** Connected device — drives the stored-credentials read-back. */
  device: SoluuDevice;
  initialSsid?: string;
  /**
   * Prefill for the password field. In Device+AP mode the harness owns
   * the AP's WPA passphrase so we can prefill it; in Device mode the
   * tester types their own WiFi password and this stays empty.
   */
  initialPassword?: string;
  busy: boolean;
  /**
   * If non-null, the form is greyed out and the reason is shown as a hint.
   * Used by Device + AP mode to make it obvious that a scenario must be
   * picked before submitting (otherwise the submit was silently a no-op).
   */
  disabledReason?: string | null;
  /** Forwarded to the stored-credentials read-back; see StoredCredentialsReadout. */
  onCredentialReadAnomaly?(input: Pick<AttemptAnomaly, 'kind' | 'message'>): void;
  onSubmit(ssid: string, password: string): void;
}

const MAX_BYTES = 50;

function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

export function WifiConfigForm({
  device,
  initialSsid = '',
  initialPassword = '',
  busy,
  disabledReason = null,
  onCredentialReadAnomaly,
  onSubmit,
}: Props) {
  const [ssid, setSsid] = useState(initialSsid);
  const [password, setPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(false);

  const ssidBytes = utf8Bytes(ssid);
  const pwBytes = utf8Bytes(password);
  const ssidOk = ssid.length > 0 && ssidBytes <= MAX_BYTES;
  const pwOk = pwBytes <= MAX_BYTES;
  const isDisabled = disabledReason !== null;
  const canSubmit = ssidOk && pwOk && !busy && !isDisabled;

  return (
    <section className="section">
      <h2>WiFi Configuration</h2>
      <StoredCredentialsReadout
        device={device}
        onReadFailureAnomaly={onCredentialReadAnomaly}
      />
      {disabledReason && (
        <p className="muted form-disabled-hint">{disabledReason}</p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(ssid, password);
        }}
      >
        <div className="field">
          <label htmlFor="ssid">SSID ({ssidBytes}/{MAX_BYTES} bytes)</label>
          <input
            id="ssid"
            type="text"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={isDisabled}
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password ({pwBytes}/{MAX_BYTES} bytes)</label>
          <div className="password-row">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={isDisabled}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              disabled={isDisabled}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <button type="submit" className="primary" disabled={!canSubmit}>
          {busy ? 'Connecting…' : 'Connect'}
        </button>
        {!ssidOk && ssid.length > 0 && !isDisabled && (
          <p className="muted">SSID exceeds 50-byte limit.</p>
        )}
        {!pwOk && !isDisabled && (
          <p className="muted">Password exceeds 50-byte limit.</p>
        )}
      </form>
    </section>
  );
}
